import Dexie, { Table, UpdateSpec } from 'dexie';
import { Injectable } from '@angular/core';
import { TableNames } from '../tables/table-names.enum';
import { EventTracking } from '../tables/event-tracking.interface';
import { TableMap } from '../tables/table-map';
import { StorageStrategy } from '../storage-strategy.interface';

@Injectable({ providedIn: 'root' })
export class DexieService extends Dexie implements StorageStrategy<TableMap> {

  [TableNames.EventTrackings]!: Dexie.Table<EventTracking, number>;

  constructor() {
    super('wakanda-db');
      this.version(1).stores({
        [TableNames.EventTrackings]: '++id, eventName, createdAt',
      });

      this[TableNames.EventTrackings] = this.table(TableNames.EventTrackings);
  }

  private getTable<K extends keyof TableMap>(table: K): Table<TableMap[K], number> {
    const tbl = this[table];
    if (!tbl) {
      throw new Error(`Table "${String(table)}" is not initialized in DexieService.`);
    }
    return tbl as Table<TableMap[K], number>;
  }

  async add<K extends keyof TableMap>(table: K, data: TableMap[K]): Promise<number> {
    return this.getTable(table).add(data);
  }

  async getByField<K extends keyof TableMap>(table: K, field: keyof TableMap[K], value: any): Promise<TableMap[K] | undefined> {
    return this.getTable(table).where(field as string).equals(value).first();
  }

  async getAll<K extends keyof TableMap>(table: K): Promise<TableMap[K][]> {
    return this.getTable(table).toArray();
  }

  async getById<K extends keyof TableMap>(table: K, id: number): Promise<TableMap[K] | undefined> {
    return this.getTable(table).get(id);
  }

  async update<K extends keyof TableMap>(
    table: K,
    id: number,
    changes: Partial<TableMap[K]>
  ): Promise<number> {
    return this.getTable(table).update(id, changes as UpdateSpec<TableMap[K]>);  
  }

  async deleteById<K extends keyof TableMap>(table: K, id?: number): Promise<void> {
    await this.getTable(table).delete(id);
  }

  async clear<K extends keyof TableMap>(table: K): Promise<void> {
    await this.getTable(table).clear(); // delete all rows
  }

  async clearAndInsert<K extends keyof TableMap>(table: K, data: TableMap[K]): Promise<void> {
    await this.getTable(table).clear();
    await this.getTable(table).add(data);
  }

  async getEventByEventName<K extends keyof TableMap>(table: K, eventName: string): Promise<TableMap[K] | undefined> {
    return this.getTable(table).where('eventName').equals(eventName).first();
  }

  async upsert<K extends keyof TableMap>(
    table: K,
    whereField: keyof TableMap[K],
    whereValue: any,
    payload: TableMap[K]
  ): Promise<void> {
    const tbl = this.getTable(table); // get actual Table instance
  
    const existing = await tbl
      .where(whereField as string) // typecast to string for Dexie
      .equals(whereValue)
      .first();
  
    const record = {
      ...payload,
      createdAt: new Date()
    };
  
    if (existing?.id) {
      await this.update(table, existing.id, record);
    } else {
      await this.add(table, record);
    }
  }
  
}
